import React, { useState, useContext } from 'react';
import { Box, Typography, Button, Menu } from '@mui/material';
import theme from '../../constants/theme';
import { device } from '../../constants/devices';
import FilterIcon from '../../assets/img/filter.svg';
import { InputsSection } from './sections/inputs';
import { TitleAndChips } from './sections/titleAndChips';
import { Accordion } from '../accordion';
import { FilledButtonStyled } from '../button/filledButtonStyled';
import { useIsWhichSize } from '../../hooks/useIsWhichSize';
import { AnchorEls, Section, Selected } from './props';
import { zIndexLevels } from '../../constants/zIndexLevels';
import { t } from '../../utils/translate';
import { useFilterUpdateWithSections } from '../../utils/filterUpdate';
import { FilterContext, FilterProps } from '../../context/filter';

export const Filter = () => {
  const { isMobile } = useIsWhichSize();
  const setFilter = (window as any).setFilter;
  const removeFilter = (window as any).removeFilter;
  const [anchorEls, setAnchorEls] = useState<AnchorEls>({
    department: null,
    locations: null,
    categories: null,
    brand: null,
  });
  const context = useContext(FilterContext);
  const {
    department,
    setDepartment,
    removeChips,
    categories,
    setCategories,
    locations,
    setLocations,
    brand,
    setBrand,
  } = context;

  const items: Array<Section> = [
    {
      name: t('Tätigkeitsbereich'),
      options: (window as any).cf_department,
      open: !!anchorEls.department,
      anchorEl: anchorEls.department,
      action: (e: React.MouseEvent<HTMLButtonElement>) => {
        handleMenuClick(e, 'department');
      },
      handleClose: () => {
        setAnchorEls({ ...anchorEls, department: null });
        patchFilter(setDepartment, 'arr', department.filtered);
      },
      arrSelected: department.arr,
      setArrOptions: (value) => patchFilter(setDepartment, 'arr', value),
      filter: () =>
        setFilterValue(
          department.arr,
          (value) => patchFilter(setDepartment, 'filtered', value),
          department.facet,
          'department'
        ),
      filteredArr: department.filtered,
      removeChips: (removeAll: boolean, label?: string) => {
        const arr = removeChips(
          department.arr,
          setDepartment,
          removeAll,
          label
        );
        setFilterInUrl(arr, department.facet);
      },
    },
    {
      name: t('Einstiegsart'),
      options: (window as any).cf_category,
      open: !!anchorEls.categories,
      anchorEl: anchorEls.categories,
      action: (e: React.MouseEvent<HTMLButtonElement>) => {
        handleMenuClick(e, 'categories');
      },
      handleClose: () => {
        setAnchorEls({ ...anchorEls, categories: null });
        patchFilter(setCategories, 'arr', categories.filtered);
      },
      arrSelected: categories.arr,
      setArrOptions: (value) => patchFilter(setCategories, 'arr', value),
      filter: () =>
        setFilterValue(
          categories.arr,
          (value) => patchFilter(setCategories, 'filtered', value),
          categories.facet,
          'categories'
        ),
      filteredArr: categories.filtered,
      removeChips: (removeAll: boolean, label?: string) => {
        const arr = removeChips(
          categories.arr,
          setCategories,
          removeAll,
          label
        );
        setFilterInUrl(arr, categories.facet);
      },
    },
    {
      name: t('Standort'),
      options: (window as any).location_city,
      open: !!anchorEls.locations,
      anchorEl: anchorEls.locations,
      action: (e: React.MouseEvent<HTMLButtonElement>) => {
        handleMenuClick(e, 'locations');
      },
      handleClose: () => {
        setAnchorEls({ ...anchorEls, locations: null });
        patchFilter(setLocations, 'arr', locations.filtered);
      },
      arrSelected: locations.arr,
      setArrOptions: (value) => patchFilter(setLocations, 'arr', value),
      filter: () =>
        setFilterValue(
          locations.arr,
          (value) => patchFilter(setLocations, 'filtered', value),
          locations.facet,
          'locations'
        ),
      filteredArr: locations.filtered,
      removeChips: (removeAll: boolean, label?: string) => {
        const arr = removeChips(locations.arr, setLocations, removeAll, label);
        setFilterInUrl(arr, locations.facet);
      },
    },
    {
      name: t('Unternehmen'),
      options: (window as any).cf_brand,
      open: !!anchorEls.brand,
      anchorEl: anchorEls.brand,
      action: (e: React.MouseEvent<HTMLButtonElement>) => {
        handleMenuClick(e, 'brand');
      },
      handleClose: () => {
        setAnchorEls({ ...anchorEls, brand: null });
        patchFilter(setBrand, 'arr', brand.filtered);
      },
      arrSelected: brand.arr,
      setArrOptions: (value) => patchFilter(setBrand, 'arr', value),
      filter: () =>
        setFilterValue(
          brand.arr,
          (value) => patchFilter(setBrand, 'filtered', value),
          brand.facet,
          'brand'
        ),
      filteredArr: brand.filtered,
      removeChips: (removeAll: boolean, label?: string) => {
        const arr = removeChips(brand.arr, setBrand, removeAll, label);
        setFilterInUrl(arr, brand.facet);
      },
    },
  ];

  const patchFilter = (
    method: React.Dispatch<React.SetStateAction<FilterProps>>,
    field: string,
    value: Selected[]
  ) => {
    method((prevState: FilterProps) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const setFilterValue = (
    arr: Array<Selected>,
    setFiltered: (value: Selected[]) => void,
    field: string,
    anchor: string
  ) => {
    setFilterInUrl(arr, field);
    setFiltered(arr);
    setAnchorEls({ ...anchorEls, [anchor]: null });
  };

  const send = () => {
    setFilterInUrl(department.arr, department.facet);
    setFilterInUrl(categories.arr, categories.facet);
    setFilterInUrl(locations.arr, locations.facet);
    setFilterInUrl(brand.arr, brand.facet);
    closeFilterMobile();

    patchFilter(setDepartment, 'filtered', department.arr);
    patchFilter(setCategories, 'filtered', categories.arr);
    patchFilter(setLocations, 'filtered', locations.arr);
    patchFilter(setBrand, 'filtered', brand.arr);
  };

  const setAndCloseFilterMobile = () => {
    closeFilterMobile();

    patchFilter(setDepartment, 'arr', department.filtered);
    patchFilter(setCategories, 'arr', categories.filtered);
    patchFilter(setLocations, 'arr', locations.filtered);
    patchFilter(setBrand, 'arr', brand.filtered);
  };

  const setFilterInUrl = (arr: Array<Selected>, name: string) => {
    arr.length
      ? setFilter(
          name,
          arr.map((a) => a.value),
          'all'
        )
      : removeFilter(name);
  };

  const closeFilterMobile = () => {
    let element = document.getElementsByClassName('sui-layout-sidebar')[0];
    (element as HTMLElement).style.display = 'none';
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    anchorKey: string
  ) => {
    let updatedAnchorEls: AnchorEls = {};

    Object.keys(anchorEls).forEach((key) => {
      updatedAnchorEls[key] = key === anchorKey ? event.currentTarget : null;
    });

    setAnchorEls(updatedAnchorEls);
  };

  const isAnyMenuOpen = (): boolean => {
    return (
      !!anchorEls.department ||
      !!anchorEls.locations ||
      !!anchorEls.categories ||
      !!anchorEls.brand
    );
  };

  useFilterUpdateWithSections(
    categories.isOnInit,
    categories.facet,
    setCategories
  );

  useFilterUpdateWithSections(
    department.isOnInit,
    department.facet,
    setDepartment
  );

  useFilterUpdateWithSections(brand.isOnInit, brand.facet, setBrand);

  useFilterUpdateWithSections(
    locations.isOnInit,
    locations.facet,
    setLocations
  );

  return (
    <Box
      zIndex={isAnyMenuOpen() ? zIndexLevels.filter : 'initial'}
      position="relative"
      sx={{
        height: isMobile ? 'calc(100vh - 144px)' : 'initial',
        overflow: 'auto',
      }}
    >
      {!isMobile ? (
        <Typography
          color={isAnyMenuOpen() ? 'secondary' : 'primary'}
          fontWeight={isAnyMenuOpen() ? 500 : 400}
          variant="h3"
          sx={{ marginBottom: '16px' }}
        >
          {t('Ergebnisse filtern')}
        </Typography>
      ) : (
        <Box
          display="flex"
          gap={1}
          mb={3}
          mt={3}
          ml={2}
          onClick={setAndCloseFilterMobile}
        >
          <img src={FilterIcon} alt="Stellen filtern" />
          <Typography
            variant="subtitle1"
            color="primary"
            fontWeight={600}
            sx={{ textDecoration: 'underline' }}
          >
            {t('schließen')}
          </Typography>
        </Box>
      )}

      {items &&
        items.map((item: Section, index) => (
          <React.Fragment key={index}>
            {!isMobile ? (
              <Box>
                <Box>
                  <Button
                    aria-controls={item.open ? 'fade-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={item.open ? 'true' : undefined}
                    onClick={item.action}
                    disableRipple
                    sx={{
                      display: 'block',
                      cursor: 'pointer !important',
                      borderBottom: '1px solid #CCC',
                      minHeight: '57px',
                      width: '100%',
                      padding: '16px 14px',
                      borderRadius: 0,
                      textTransform: 'initial',
                      color: item.open
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                      backgroundColor: item.open
                        ? theme.palette.light.main
                        : theme.palette.secondary.main,
                      '&:hover': {
                        backgroundColor: item.open
                          ? theme.palette.light.main
                          : theme.palette.secondary.main,
                      },
                    }}
                  >
                    <TitleAndChips
                      open={item.open}
                      name={item.name}
                      removeChips={item.removeChips}
                      arr={item.filteredArr}
                    />
                  </Button>
                </Box>

                <Menu
                  MenuListProps={{
                    'aria-labelledby': 'fade-button',
                  }}
                  anchorEl={item.anchorEl}
                  open={item.open || false}
                  onClose={item.handleClose}
                  transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                  sx={{
                    marginTop: '-25px',
                    '.MuiPaper-root': {
                      padding: '32px 32px 0 32px',
                      border: 'unset !important',
                      background: '#FFF !important',
                      maxWidth: '704px !important',
                      width: 'fit-content !important',
                      marginLeft: '16px',
                    },
                    ul: { padding: 0 },
                    [device.small]: {
                      width: 'unset !important',
                    },
                  }}
                >
                  <InputsSection item={item} isMobile={false} />
                </Menu>
              </Box>
            ) : (
              <Accordion
                header={
                  <TitleAndChips
                    open={item.open}
                    name={item.name}
                    removeChips={item.removeChips}
                    arr={item.filteredArr}
                    isMobile
                  />
                }
              >
                <InputsSection item={item} isMobile />
              </Accordion>
            )}
          </React.Fragment>
        ))}
      {isMobile ? (
        <Box
          display="flex"
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
          gap={2}
          height="144px"
          boxShadow="0 -10px 20px 0 rgba(0, 0, 0, 0.15)"
          width="100%"
          position="fixed"
          bottom={0}
          padding="0 16px"
          sx={{
            backgroundColor: theme.palette.secondary.main,
          }}
        >
          <FilledButtonStyled
            onClick={send}
            disableRipple
            sx={{ width: '100%', fontWeight: 600 }}
          >
            {t('Anwenden')}
          </FilledButtonStyled>
          <Box onClick={setAndCloseFilterMobile}>
            <Typography
              variant="h5"
              color="primary"
              fontWeight={600}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              {t('Abbrechen')}
            </Typography>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};
